import React, { useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { MenuItem, useMediaQuery, useTheme } from '@material-ui/core';

import { addNotificationAction } from '../../action/notifications/notifications';
import { userSelector } from '../../reducers/current-user-selectors';

import { LegacyAnnouncementComposer } from '../../employee-messages/legacy/legacy-announcement-composer';
import { NewMessageComposer } from '../../employee-messages/new-message-composer/new-message-composer';
import { FeatureFlagged, FeatureGatedVariant } from '../../feature-gates/feature-flagged';
import { FeatureGates } from '../../feature-gates/feature-gates';
import { UiDialog } from '../../ui/ui-dialog';
import { UiKebabMenu } from '../../ui/ui-kebab-menu';
import { UiMessageBanner } from '../../ui/ui-message-banner';
import { UiPopover } from '../../ui/ui-popover';

import { capitalize, formatPhoneNumber } from '../../util/formatters';
import { getCustomerLogoPath } from '../../util/get-customer-logo-path';
import { MaterialDesignIcon, ICON_NAMES } from '../../util/material-design-icons';

type PopoverVariant = 'employee' | 'applicant' | 'survey';
const POPOVER_VARIANT: Record<string, PopoverVariant> = {
  EMPLOYEE: 'employee',
  APPLICANT: 'applicant',
  SURVEY: 'survey',
} as const;

export const VCardPopoverIcon = ({
  org,
  variant,
  onClose,
  isOpen,
}: {
  org: Org | null;
  variant: PopoverVariant;
  onClose: () => void;
  isOpen: boolean;
}) => {
  const popoverAnchorRef = useRef<HTMLDivElement | null>(null);
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);

  const theme = useTheme();
  const isOnSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const dispatch = useDispatch();
  const handleAnnouncementSent = () => {
    closeShareDialog();
    dispatch(
      addNotificationAction({
        key: 'share-contact-information',
        message: `Announcement with ${variant} number sent`,
      }),
    );
  };

  const user = useSelector((state: any) => userSelector(state));

  let phone: string | null | undefined;
  switch (variant) {
    case POPOVER_VARIANT.EMPLOYEE: {
      phone = org?.phoneNumber;
      break;
    }
    case POPOVER_VARIANT.APPLICANT: {
      phone = org?.jobPhoneNumber;
      break;
    }
    case POPOVER_VARIANT.SURVEY: {
      phone = org?.surveyPhoneNumber;
      break;
    }
    default:
      break;
  }

  if (!org || !phone || isOnMobile) {
    return null;
  }

  const showContactCardPopover = Boolean(isOpen && !isOnSmallScreen && !showShareDialog);
  const showContactCardDialog = Boolean(isOpen && isOnSmallScreen && !showShareDialog);

  const openShareDialog = () => {
    setShowShareDialog(true);
  };

  const closeShareDialog = (e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setShowShareDialog(false);
    onClose();
  };

  const initialMessage = `Hi {{{employee.casualName}}} - This is ${
    user.firstName
  } at {{{employee.companyName}}}. ${
    variant === POPOVER_VARIANT.EMPLOYEE
      ? `We'll be reaching out from this number in the future, so please save our contact information in your phone.`
      : variant === POPOVER_VARIANT.SURVEY
      ? `${formatPhoneNumber(
          org.surveyPhoneNumber,
        )} is the employee survey number we'll be using, please save the contact information in your phone. You&apos;ll receive texts from time to time asking for your thoughts. Thank you for taking the time to share!
      \n\nSome surveys will be marked “anonymous.” These will not display your name or number with your response in order to create a safe space to share your thoughts and opinions.
      \n\nOther surveys may not be anonymous; We automatically receive your name and number with your response so that we can follow up if helpful.`
      : ''
  }`;

  const renderContent = () => (
    <div className="u-margin u-width-300px">
      <div className="u-margin-bottom">Sending {variant} texts as: </div>
      <div className="u-border u-border-neutral3 u-background-neutral2 u-padding-lg u-spread">
        <div className="u-flex-center">
          <img
            src={`${getCustomerLogoPath(org)}`}
            className="u-width-50px u-height-50px u-scaleDown u-borderRadius-50 u-border u-background-white"
            alt={org.name}
          />
          <div className="u-margin-left-lg u-flexColumn">
            {org.name}
            <a href={`tel:+1${phone}`}>{formatPhoneNumber(phone)}</a>
          </div>
        </div>
        {variant !== POPOVER_VARIANT.APPLICANT && (
          <UiKebabMenu menuId={`${variant}-contact-card-menu`} stopPropagation>
            <MenuItem onClick={openShareDialog}>Share</MenuItem>
          </UiKebabMenu>
        )}
      </div>
      {variant === POPOVER_VARIANT.APPLICANT && (
        <div className="u-margin-top">
          Applicants can text <span className="typo-bold">apply</span> to this number to get an
          application link.
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        ref={popoverAnchorRef}
        className={`${
          variant === POPOVER_VARIANT.APPLICANT ? 'u-margin-right-lg-neg' : 'u-margin-left-lg-neg'
        } u-padding-x`}
      >
        <MaterialDesignIcon
          iconName={ICON_NAMES.INFO}
          color="gray"
          className="u-colorHover-secondary u-margin-top-sm"
        />
        {showContactCardPopover && (
          <UiPopover
            anchorRef={popoverAnchorRef}
            isShowingPopover={isOpen}
            onClose={onClose}
            margins={{ marginTop: -4, marginLeft: variant === POPOVER_VARIANT.SURVEY ? -28 : 0 }}
          >
            {renderContent()}
          </UiPopover>
        )}
        {showContactCardDialog && (
          <UiDialog onClose={onClose} title={`${capitalize(variant)} Contact Information`}>
            <div className="u-margin-y-lg u-margin-x-2x">{renderContent()}</div>
          </UiDialog>
        )}
      </div>
      {showShareDialog && (
        <UiDialog onClose={closeShareDialog} title="Share Company Contact Information" size="sm">
          {variant === POPOVER_VARIANT.EMPLOYEE && (
            <UiMessageBanner className="u-margin">
              <div className="u-flexColumn">
                <div>
                  Send this announcement with your contact card to make it easy for your employees
                  to save the number to their phones
                </div>
                <div className="u-margin-top-sm typo-subtitle">
                  This option can be found in the drop down next to the send button
                </div>
              </div>
            </UiMessageBanner>
          )}
          <FeatureFlagged
            flagName={FeatureGates.INTEGRATED_MESSAGING}
            variant={FeatureGatedVariant.HIDE_ON_FLAG}
          >
            <LegacyAnnouncementComposer
              defaultToAllEmployees
              hideThread
              initialMessage={initialMessage}
              onMessageSent={handleAnnouncementSent}
              orgId={org.org_id}
              onClose={closeShareDialog}
            />
          </FeatureFlagged>
          <FeatureFlagged
            flagName={FeatureGates.INTEGRATED_MESSAGING}
            variant={FeatureGatedVariant.SHOW_ON_FLAG}
          >
            <NewMessageComposer
              orgId={org.org_id}
              onMessageSent={handleAnnouncementSent}
              onClose={closeShareDialog}
              initialMessage={initialMessage}
              hideThread
              hideEmployeeOptions
              defaultToAllEmployees
            />
          </FeatureFlagged>
        </UiDialog>
      )}
    </>
  );
};
